import { useState } from 'react';
import { Link } from 'react-router';
import type { Route } from './+types/contact';
import { apiFetch } from '~/lib/api-client';
import { postJson, errorMessage } from '~/lib/http';
import type { ApiResult, SiteConfig } from '~/lib/types';
import { Turnstile } from '~/components/turnstile';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';

const SUBJECTS = ['General', 'Advertising', 'Suggest an event', 'Correction', 'Other'] as const;

export function meta(_: Route.MetaArgs) {
  return [
    { title: 'Contact — Tampa Bay Networking Calendar' },
    {
      name: 'description',
      content:
        'Questions, corrections, advertising, or an event we missed — send a note. A real person reads every message.',
    },
    { property: 'og:image', content: '/og-default.png' },
  ];
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const raw = url.searchParams.get('subject') ?? '';
  const subject = (SUBJECTS as readonly string[]).includes(raw) ? raw : 'General';

  let turnstileSiteKey = '';
  try {
    const res = await apiFetch(request, '/api/config');
    const body = (await res.json().catch(() => null)) as ApiResult<SiteConfig> | null;
    if (body?.ok) turnstileSiteKey = body.data.turnstileSiteKey ?? '';
  } catch {
    // config unavailable — skip the widget
  }
  return { turnstileSiteKey, subject };
}

export default function Contact({ loaderData }: Route.ComponentProps) {
  const { turnstileSiteKey, subject: initialSubject } = loaderData;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await postJson('/api/contact', {
      name,
      email,
      phone,
      organization,
      subject,
      message,
      website: honeypot,
      ...(token ? { turnstileToken: token } : {}),
    });
    setBusy(false);
    if (res.ok) setSent(true);
    else setError(errorMessage(res.error));
  }

  if (sent) {
    return (
      <main className="page">
        <div className="container page-narrow">
          <div className="card card-pad-lg stack success-screen">
            <div className="success-check" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24">
                <path
                  d="M4 12.5l5 5L20 6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1>Got it — thanks.</h1>
            <p className="muted">
              A real person reads every message. Expect a reply within a business day or two.
            </p>
            <div className="row">
              <Link to="/" className="ui-btn btn-primary">
                Back to the calendar
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="container page-narrow">
        <section className="masthead">
          <h1>Say hello</h1>
          <p className="masthead-tagline">
            Corrections, questions, advertising, an event we're missing — it all lands in the same
            inbox, read by a person.
          </p>
        </section>

        <form className="card stack" onSubmit={send}>
          <div className="form-grid">
            <div className="field">
              <Label htmlFor="c-name">Your name *</Label>
              <Input
                id="c-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </div>
            <div className="field">
              <Label htmlFor="c-email">Your email *</Label>
              <Input
                id="c-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="field">
              <Label htmlFor="c-phone">Phone</Label>
              <Input
                id="c-phone"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
            </div>
            <div className="field">
              <Label htmlFor="c-org">Organization</Label>
              <Input
                id="c-org"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                autoComplete="organization"
              />
            </div>
            <div className="field span-2">
              <Label htmlFor="c-subject">Subject</Label>
              <select
                id="c-subject"
                className="ui-select"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="field span-2">
              <Label htmlFor="c-message">Message *</Label>
              <textarea
                id="c-message"
                className="ui-textarea"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Honeypot — humans never see this */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="c-website-hp">Website</label>
            <input
              id="c-website-hp"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <Turnstile siteKey={turnstileSiteKey} onToken={setToken} />

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <div>
            <button type="submit" className="ui-btn btn-primary btn-lg" disabled={busy}>
              {busy ? 'Sending…' : 'Send message'}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
